import { useMemo } from 'react'
import { useDataSource } from '../../../../../lib/dataSource'
import { isCompletedRound } from '../../../../../lib/db/taskFilters'
import { useLifeSpaceTasks } from '../../../hooks/useLifeSpaceTasks'
import { useListedChildren } from '../../../hooks/useListedChildren'
import type { ListedTask } from '../../../utils/taskToEntryData'

interface UseDoneTasksArgs {
  lifeSpace: 'personal' | 'work' | 'home'
  isActive: boolean
}

/**
 * Alle erledigten Aufgaben eines Lebensbereichs, zuletzt Erledigtes zuerst —
 * die Daten der Seite **Geschafft** (CONTEXT.md).
 *
 * **Alle, nicht ein Fenster.** Die drei Zahlen über der Liste rechnen aus
 * dieser Menge, und die **Serie** darf nicht an einem Ladefenster enden — 45
 * Tage aus 30 Tagen ergäben 30. Was die Seite davon ZEIGT, entscheidet sie
 * selbst beim Scrollen.
 *
 * Aufgaben ohne Projekt kommen über `task.lifeSpace` mit, so wie bei
 * **Ruhend** — damit hat auch die abgehakte Aufgabe aus **Demnächst** wieder
 * einen Ort, an dem man sie findet.
 *
 * **Erledigte Runden einer wiederkehrenden Unteraufgabe** (#205) stehen mit dem Titel ihrer
 * Aufgabe davor und zählen für Zahlen und Serie — nicht, wenn die Aufgabe sie mitgenommen hat.
 */
export function useDoneTasks({ lifeSpace, isActive }: UseDoneTasksArgs): ListedTask[] | undefined {
  const tasks = useListedChildren(useLifeSpaceTasks(lifeSpace, useDataSource().useCompletedTasks(isActive)), isCompletedRound)

  return useMemo(() => {
    if (!tasks) return undefined
    return [...tasks].sort((a, b) => {
      const aMs = a.completedAt ? a.completedAt.getTime() : 0
      const bMs = b.completedAt ? b.completedAt.getTime() : 0
      return bMs - aMs
    })
  }, [tasks])
}
